import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Search, BookOpen, BarChart3, Cloud, Wind, Thermometer, Droplets, Eye } from 'lucide-react'
import { API_BASE } from '../App'

function HomePage() {
  const [papers, setPapers] = useState([])
  const [categories, setCategories] = useState(['全部'])
  const [category, setCategory] = useState('全部')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)

  const limit = 10

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/categories`)
      const data = await res.json()
      if (data.categories) setCategories(['全部', ...data.categories])
    } catch (e) {}
  }, [])

  const fetchPapers = useCallback(async () => {
    setLoading(true)
    try {
      const cat = category === '全部' ? '' : category
      const res = await fetch(
        `${API_BASE}/api/papers?page=${page}&limit=${limit}&category=${encodeURIComponent(cat)}&search=${encodeURIComponent(search)}`
      )
      const data = await res.json()
      setPapers(data.data || [])
      setTotal(data.pagination?.total || 0)
    } catch (e) {
      console.error('Fetch error:', e)
    }
    setLoading(false)
  }, [category, search, page])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  useEffect(() => {
    fetchPapers()
  }, [fetchPapers])

  const categoryIcons = {
    '天气学': Cloud,
    '气候学': Thermometer,
    '大气物理学': Wind,
    '海洋气象': Droplets,
    '数值预报': BarChart3,
    '卫星遥感': Eye,
    '全部': BookOpen
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <header className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-3 mb-4">
            <Cloud className="w-10 h-10 text-primary-300" />
            <h1 className="text-4xl font-bold tracking-tight">枫叶沉迷的小屋</h1>
          </div>
          <p className="text-xl text-primary-200 max-w-2xl">
            Atmospheric · Railway · Physics
          </p>

          {/* AI Analysis Banner — removed */}

          <div className="grid grid-cols-2 gap-6 mt-10 max-w-md">
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-3xl font-bold">{total}</div>
              <div className="text-primary-200 text-sm">收录文献</div>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-4">
              <div className="text-3xl font-bold">{categories.length - 1}</div>
              <div className="text-primary-200 text-sm">学科分类</div>
            </div>
          </div>
        </div>
      </header>

      {/* Search & Filter Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="搜索文献标题、作者、关键词..."
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white transition-all"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0">
              {categories.map(cat => {
                const Icon = categoryIcons[cat] || BookOpen
                return (
                  <button
                    key={cat}
                    onClick={() => { setCategory(cat); setPage(1) }}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl whitespace-nowrap transition-all ${
                      category === cat 
                        ? 'bg-primary-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Papers List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : papers.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">暂无文献，请前往管理后台添加</p>
            <Link to="/admin" className="btn-primary inline-block mt-4">前往管理后台</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {papers.map(paper => (
              <div key={paper.id} className="card hover:shadow-md transition-all group">
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2.5 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full">
                        {paper.category}
                      </span>
                      <span className="text-gray-400 text-sm">{paper.year}</span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors mb-1">
                      {paper.title_cn || paper.title}
                    </h2>
                    <p className="text-gray-500 text-sm mb-3">{paper.title}</p>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                      {paper.abstract_cn || paper.abstract}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{paper.authors}</span>
                      <span className="text-gray-300">|</span>
                      <span>{paper.journal}</span>
                      {paper.doi && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-primary-600">DOI: {paper.doi}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex md:flex-col gap-2">
                    <Link 
                      to={`/paper/${paper.id}`}
                      className="btn-primary flex items-center gap-2"
                    >
                      查看详情
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                    {paper.pdf_aliyun_url && (
                      <a 
                        href={paper.pdf_aliyun_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Cloud className="w-4 h-4" />
                        阿里云盘
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
            >
              上一页
            </button>
            <span className="px-4 py-2 text-gray-600">
              {page} / {totalPages}
            </span>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
            >
              下一页
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-500 text-sm">
          <p>枫叶沉迷的小屋 · Atmospheric · Railway · Physics</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/admin" className="text-primary-600 hover:underline">管理后台</Link>
            <Link to="/visualizations" className="text-primary-600 hover:underline">可视化中心</Link>
            <Link to="/learn" className="text-primary-600 hover:underline">Learn 工具</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage
